/**
 * Vincent #119 (Eric 2026-05-13) — Page Procédures globale (parité onglet 4. Scripts).
 * Liste verticale expandable, chaque procédure révèle son contenu détaillé,
 * boutons d'édition inline (titre + contenu), génération auto de titre IA,
 * historique des titres avec rollback. Remplace l'ancien wrapper KnowledgePage.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import { useLang } from "../contexts/LanguageContext";
import {
  ClipboardText, MagnifyingGlass, CaretDown, CaretRight, PencilSimple,
  Trash, CheckCircle, X as XIcon, Sparkle, ArrowCounterClockwise,
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

export default function ProceduresPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [procedures, setProcedures] = useState([]);
  const [videosById, setVideosById] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | accepted
  const [sortBy, setSortBy] = useState("chrono"); // alpha | chrono | category | video
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null); // {id, title, summary}
  const [titleAi, setTitleAi] = useState({ id: null, generating: false, proposals: [], history: [] });

  useEffect(() => { document.title = lang === "fr" ? "Procédures — Knoria" : "Procedures — Knoria"; }, [lang]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [procRes, vidsRes] = await Promise.all([
        axios.get(`${API}/api/knowledge`, { params: { type: "procedure" }, withCredentials: true }),
        axios.get(`${API}/api/videos`, { withCredentials: true }),
      ]);
      const items = Array.isArray(procRes.data) ? procRes.data : [];
      const vids = Array.isArray(vidsRes.data?.items) ? vidsRes.data.items : (Array.isArray(vidsRes.data) ? vidsRes.data : []);
      const map = {};
      vids.forEach(v => { map[v.id] = v; });
      setVideosById(map);
      setProcedures(items);
    } catch (e) {
      console.error(e);
      toast.error(lang === "fr" ? "Échec du chargement" : "Load failed");
      setProcedures([]);
    } finally { setLoading(false); }
  }, [lang]);
  useEffect(() => { load(); }, [load]);

  const fmtTs = (sec) => {
    if (sec == null || isNaN(sec)) return "";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    const items = procedures.filter(p => {
      if (q && !((p.title || "").toLowerCase().includes(q)) && !((p.content?.summary || "").toLowerCase().includes(q))) return false;
      if (filter === "pending" && p.status !== "pending_review") return false;
      if (filter === "accepted" && p.status !== "approved") return false;
      return true;
    });
    const collator = new Intl.Collator(lang === "fr" ? "fr" : "en", { sensitivity: "base" });
    return items.slice().sort((a, b) => {
      if (sortBy === "alpha") {
        return collator.compare(a.title || "", b.title || "");
      }
      if (sortBy === "category") {
        const ca = (videosById[a.video_id]?.category || videosById[a.video_id]?.training_theme || "").toLowerCase();
        const cb = (videosById[b.video_id]?.category || videosById[b.video_id]?.training_theme || "").toLowerCase();
        if (ca !== cb) return collator.compare(ca, cb);
        return collator.compare(a.title || "", b.title || "");
      }
      if (sortBy === "video") {
        const va = (videosById[a.video_id]?.title || "").toLowerCase();
        const vb = (videosById[b.video_id]?.title || "").toLowerCase();
        if (va !== vb) return collator.compare(va, vb);
        return collator.compare(a.title || "", b.title || "");
      }
      // chrono — most recent first
      const da = new Date(a.created_at || 0).getTime();
      const dbb = new Date(b.created_at || 0).getTime();
      return dbb - da;
    });
  }, [procedures, search, filter, sortBy, videosById, lang]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const startEdit = async (p) => {
    setEditing({ id: p.id, title: p.title || "", summary: p.content?.summary || "" });
    setTitleAi({ id: p.id, generating: false, proposals: [], history: [] });
    try {
      const { data } = await axios.get(`${API}/api/agent/knowledge-units/${p.id}/history`, { withCredentials: true });
      const titleEntries = (data?.edit_history || [])
        .filter(e => (e.fields_changed || []).includes("title") && (e.previous_title || "").trim())
        .map(e => ({ title: e.previous_title, at: e.edited_at }));
      setTitleAi(s => ({ ...s, history: titleEntries.slice(-20) }));
    } catch {}
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await axios.patch(`${API}/api/agent/knowledge-units/${editing.id}`, {
        title: editing.title,
        content: { summary: editing.summary },
      }, { withCredentials: true });
      toast.success(lang === "fr" ? "Procédure mise à jour" : "Procedure updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
    }
  };

  const remove = async (p) => {
    const ok = await confirmDialog(
      lang === "fr" ? `Supprimer la procédure « ${p.title} » ?` : `Delete procedure "${p.title}"?`,
      { danger: true, confirmLabel: lang === "fr" ? "Supprimer" : "Delete" },
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/agent/knowledge-units/${p.id}`, { withCredentials: true });
      toast.success(lang === "fr" ? "Procédure supprimée" : "Procedure deleted");
      load();
    } catch (err) { toast.error("Erreur"); }
  };

  const generateTitle = async (id) => {
    if (titleAi.generating) return;
    setTitleAi(s => ({ ...s, id, generating: true }));
    try {
      const { data } = await axios.post(`${API}/api/agent/knowledge-units/${id}/generate-title`, {}, { withCredentials: true, timeout: 30000 });
      const proposals = Array.isArray(data?.proposals) ? data.proposals : (data?.title ? [data.title] : []);
      setTitleAi(s => ({ ...s, generating: false, proposals, history: Array.isArray(data?.history) ? data.history : s.history }));
      // Vincent #213 (Eric 2026-06-04) — Si IA a échoué, afficher l'erreur explicite
      if (proposals.length === 0 && data?.error) {
        toast.error(`${lang === "fr" ? "Échec IA" : "AI failed"} : ${data.error}`);
        return;
      }
      if (proposals[0]) setEditing(e => e && e.id === id ? { ...e, title: proposals[0] } : e);
      if (proposals.length) {
        toast.success(`${proposals.length} ${lang === "fr" ? "proposition(s)" : "suggestion(s)"}`);
      } else {
        toast.error(lang === "fr" ? "Aucune proposition générée — vérifiez votre Forfait AI" : "No suggestions generated — check AI Package");
      }
    } catch (err) {
      setTitleAi(s => ({ ...s, generating: false }));
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Échec" : "Failed"));
    }
  };

  // Vincent #128 (Eric 2026-05-16) — Parité avec KnowledgePage : permettre
  // d'effacer définitivement l'historique des titres pour une procédure.
  const clearTitleHistory = async (id) => {
    const ok = await confirmDialog(
      lang === "fr"
        ? "Effacer définitivement l'historique des titres précédents de cette procédure ?"
        : "Permanently delete the title history of this procedure?",
      { danger: true, confirmLabel: lang === "fr" ? "Effacer" : "Delete" },
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/agent/knowledge-units/${id}/title-history`, { withCredentials: true });
      setTitleAi(s => ({ ...s, history: [] }));
      toast.success(lang === "fr" ? "Historique effacé" : "History cleared");
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
          <ClipboardText size={24} weight="bold" />
          {lang === "fr" ? "Procédures" : "Procedures"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? "Toutes les procédures extraites des vidéos. Cliquer un titre pour voir les étapes détaillées."
            : "All procedures extracted from videos. Click a title to view detailed steps."}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={lang === "fr" ? "Rechercher…" : "Search…"}
            className="w-full pl-9 pr-3 py-2 text-sm border border-zinc-300 rounded-md focus:ring-2 focus:ring-[#002FA7]"
            data-testid="proc-search"
          />
        </div>
        {[
          { id: "all", label: lang === "fr" ? "Toutes" : "All" },
          { id: "pending", label: lang === "fr" ? "En révision" : "Pending" },
          { id: "accepted", label: lang === "fr" ? "Validées" : "Validated" },
        ].map(o => (
          <button key={o.id} onClick={() => setFilter(o.id)}
            className={`px-3 py-1.5 text-xs rounded-md border ${filter === o.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
            data-testid={`proc-filter-${o.id}`}>{o.label}</button>
        ))}
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-md border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 focus:ring-2 focus:ring-[#002FA7]"
          data-testid="proc-sort-select"
          title={lang === "fr" ? "Trier" : "Sort"}
        >
          <option value="chrono">{lang === "fr" ? "Tri : Chronologique" : "Sort: Chronological"}</option>
          <option value="alpha">{lang === "fr" ? "Tri : Alphabétique" : "Sort: Alphabetical"}</option>
          <option value="category">{lang === "fr" ? "Tri : Catégorie de fichier" : "Sort: File category"}</option>
          <option value="video">{lang === "fr" ? "Tri : Nom de vidéo" : "Sort: Video name"}</option>
        </select>
        <span className="ml-auto text-xs text-zinc-500">
          {filtered.length} / {procedures.length}
        </span>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500">{lang === "fr" ? "Chargement…" : "Loading…"}</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white border border-zinc-200 rounded-xl">
          <ClipboardText size={48} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
          <p className="font-semibold text-zinc-700">{lang === "fr" ? "Aucune procédure détectée pour le moment." : "No procedures detected yet."}</p>
          <p className="text-xs text-zinc-500 mt-1">{lang === "fr" ? "Générez des procédures depuis le détail d'une vidéo (onglet 5)." : "Generate procedures from a video detail (tab 5)."}</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="proc-list">
          {filtered.map(p => {
            const isOpen = expanded[p.id];
            const isEdit = editing?.id === p.id;
            return (
              <li key={p.id} className="border border-zinc-200 rounded-lg bg-white overflow-hidden hover:border-zinc-300 transition-colors" data-testid={`proc-item-${p.id}`}>
                <div className="flex items-center px-3 py-2 gap-2">
                  <button onClick={() => toggle(p.id)} className="text-zinc-400 hover:text-zinc-700" data-testid={`proc-toggle-${p.id}`}>
                    {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-zinc-900 truncate cursor-pointer" onClick={() => toggle(p.id)}>{p.title || (lang === "fr" ? "Sans titre" : "Untitled")}</span>
                      {p.status === "pending_review" && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{lang === "fr" ? "EN RÉVISION" : "PENDING"}</span>}
                      {p.status === "approved" && <span className="text-[10px] bg-teal-100 text-teal-800 px-1.5 py-0.5 rounded">{lang === "fr" ? "VALIDÉE" : "VALIDATED"}</span>}
                    </div>
                    <div className="text-[11px] text-zinc-500 flex items-center gap-2 mt-0.5">
                      {videosById[p.video_id]?.title && (
                        <button onClick={() => {
                          // Vincent #144 (Eric 2026-05-16) — Lien vers l'EXTRAIT
                          // précis de la vidéo où la procédure commence (param ?t=).
                          // Avant on tombait juste sur l'onglet procédures sans
                          // démarrer la vidéo au bon moment.
                          const start = Math.max(0, Math.floor(p.timestamps?.start || 0));
                          navigate(`/dashboard/videos/${p.video_id}?tab=procedures&t=${start}`);
                        }}
                          className="hover:underline truncate max-w-[300px] text-blue-600"
                          title={lang === "fr" ? `Ouvrir la vidéo au timestamp ${fmtTs(p.timestamps?.start)}` : `Open video at ${fmtTs(p.timestamps?.start)}`}
                          data-testid={`proc-video-${p.id}`}>📹 {videosById[p.video_id].title}{p.timestamps?.start != null ? ` @${fmtTs(p.timestamps.start)}` : ""} →</button>
                      )}
                      {p.timestamps?.start != null && <span className="text-zinc-400">· {fmtTs(p.timestamps.start)}</span>}
                      <span className="text-zinc-400">· {p.content?.steps?.length || 0} {lang === "fr" ? "étapes" : "steps"}</span>
                    </div>
                  </div>
                  <button onClick={() => startEdit(p)} className="text-blue-600 hover:bg-blue-50 px-2 py-1 rounded text-xs" title={lang === "fr" ? "Éditer" : "Edit"} data-testid={`proc-edit-${p.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  {/* Vincent item 6 (Eric 2026-06-04) — Mêmes boutons que Q&R :
                      Éditer (au-dessus) + Accepter + Refuser. Seulement visibles
                      en statut pending_review (sinon redondants avec le badge "VALIDÉE"). */}
                  {p.status === "pending_review" && (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            await axios.put(`${API}/api/knowledge/${p.id}/review`, { action: "approve" }, { withCredentials: true });
                            setProcedures(prev => prev.map(pp => pp.id === p.id ? { ...pp, status: "approved" } : pp));
                          } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                        }}
                        className="text-teal-600 hover:bg-teal-50 px-2 py-1 rounded text-xs"
                        title={lang === "fr" ? "Accepter" : "Approve"}
                        data-testid={`proc-approve-${p.id}`}
                      >
                        ✓
                      </button>
                      <button
                        onClick={async () => {
                          const ok = await confirmDialog(
                            lang === "fr" ? "Refuser cette procédure ? Elle sera masquée mais conservée pour audit (Loi 25)." : "Reject this procedure? It will be hidden but kept for audit (Law 25).",
                            { danger: true, confirmLabel: lang === "fr" ? "Refuser" : "Reject" },
                          );
                          if (!ok) return;
                          try {
                            await axios.put(`${API}/api/knowledge/${p.id}/review`, { action: "reject" }, { withCredentials: true });
                            setProcedures(prev => prev.map(pp => pp.id === p.id ? { ...pp, status: "rejected" } : pp));
                          } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
                        }}
                        className="text-amber-600 hover:bg-amber-50 px-2 py-1 rounded text-xs"
                        title={lang === "fr" ? "Refuser" : "Reject"}
                        data-testid={`proc-reject-${p.id}`}
                      >
                        ✗
                      </button>
                    </>
                  )}
                  <button onClick={() => remove(p)} className="text-red-500 hover:bg-red-50 px-2 py-1 rounded text-xs" title={lang === "fr" ? "Supprimer" : "Delete"} data-testid={`proc-delete-${p.id}`}>
                    <Trash size={14} />
                  </button>
                </div>

                {isEdit && (
                  <div className="px-3 py-3 bg-blue-50/50 border-t border-blue-100 space-y-2" data-testid={`proc-edit-form-${p.id}`}>
                    <div className="flex items-center gap-2">
                      <input
                        value={editing.title}
                        onChange={(e) => setEditing(s => ({ ...s, title: e.target.value }))}
                        className="flex-1 px-2 py-1 text-sm font-semibold border border-blue-300 rounded"
                        placeholder={lang === "fr" ? "Titre" : "Title"}
                        data-testid={`proc-edit-title-${p.id}`}
                      />
                      <button onClick={() => generateTitle(p.id)} disabled={titleAi.generating} className="px-2 py-1 text-[11px] rounded bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50" data-testid={`proc-gen-title-${p.id}`}>
                        <Sparkle size={12} className="inline" /> {titleAi.generating ? "..." : (lang === "fr" ? "Titre auto" : "AI title")}
                      </button>
                    </div>
                    {titleAi.id === p.id && titleAi.proposals.length > 0 && (
                      <div className="flex flex-wrap gap-1.5" data-testid={`proc-proposals-${p.id}`}>
                        {titleAi.proposals.map((t, i) => (
                          <button key={i} onClick={() => setEditing(s => ({ ...s, title: t }))}
                            className={`text-[10.5px] px-2 py-0.5 rounded border ${editing.title === t ? "bg-purple-100 border-purple-400 text-purple-900 font-semibold" : "bg-white border-zinc-300 hover:bg-purple-50"}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                    )}
                    {/* Vincent #128 (Eric 2026-05-16) — TOUJOURS afficher l'historique
                        et le bouton "Tout effacer" (même quand vide) pour la
                        découvrabilité. Vincent ne savait pas que la fonctionnalité
                        existait car le composant entier était conditionné sur
                        history.length > 0. */}
                    {titleAi.id === p.id && (
                      <div className="text-[11px] text-zinc-600 border border-zinc-200 rounded bg-zinc-50 p-2 space-y-1" data-testid={`proc-history-${p.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold inline-flex items-center gap-1">
                            <ArrowCounterClockwise size={11} />
                            🕐 {lang === "fr" ? "Historique des titres" : "Title history"} ({titleAi.history.length})
                          </span>
                          <button
                            type="button"
                            onClick={() => clearTitleHistory(p.id)}
                            disabled={titleAi.history.length === 0}
                            className="text-[10px] text-rose-600 hover:underline disabled:text-zinc-300 disabled:cursor-not-allowed disabled:no-underline"
                            data-testid={`proc-history-clear-${p.id}`}
                            title={titleAi.history.length === 0
                              ? (lang === "fr" ? "Aucun historique à effacer" : "Nothing to clear")
                              : (lang === "fr" ? "Effacer définitivement tout l'historique" : "Permanently delete history")}
                          >
                            🗑 {lang === "fr" ? "Tout effacer" : "Clear all"}
                          </button>
                        </div>
                        {titleAi.history.length === 0 ? (
                          <p className="text-[10px] text-zinc-400 italic">
                            {lang === "fr"
                              ? "Aucun titre précédent enregistré. L'historique se peuplera automatiquement à chaque sauvegarde de nouveau titre."
                              : "No previous title recorded. History will populate on each save."}
                          </p>
                        ) : (
                          <div className="ml-2 space-y-0.5">
                            {titleAi.history.slice().reverse().map((h, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => setEditing(s => ({ ...s, title: h.title }))}
                                className="block text-blue-600 hover:underline text-left"
                                data-testid={`proc-history-restore-${p.id}-${i}`}
                              >
                                ⟲ {h.title} <span className="text-[9px] text-zinc-400">{h.at?.slice(0, 10)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <textarea
                      value={editing.summary}
                      onChange={(e) => setEditing(s => ({ ...s, summary: e.target.value }))}
                      rows={6}
                      className="w-full px-2 py-1 text-xs border border-zinc-300 rounded font-mono resize-y"
                      placeholder={lang === "fr" ? "Résumé / contenu" : "Summary / content"}
                      data-testid={`proc-edit-summary-${p.id}`}
                    />
                    <div className="flex gap-2">
                      <button onClick={saveEdit} className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 inline-flex items-center gap-1" data-testid={`proc-save-${p.id}`}>
                        <CheckCircle size={12} /> {lang === "fr" ? "Sauvegarder" : "Save"}
                      </button>
                      <button onClick={() => setEditing(null)} className="px-3 py-1.5 text-xs border border-zinc-300 rounded hover:bg-zinc-50 inline-flex items-center gap-1" data-testid={`proc-cancel-${p.id}`}>
                        <XIcon size={12} /> {lang === "fr" ? "Annuler" : "Cancel"}
                      </button>
                    </div>
                  </div>
                )}

                {isOpen && !isEdit && (
                  <div className="px-4 py-3 border-t border-zinc-100 text-sm space-y-2" data-testid={`proc-detail-${p.id}`}>
                    {p.content?.summary && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-zinc-500 mb-1">{lang === "fr" ? "Résumé" : "Summary"}</h4>
                        <p className="text-zinc-700 whitespace-pre-wrap">{p.content.summary}</p>
                      </div>
                    )}
                    {Array.isArray(p.content?.steps) && p.content.steps.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-zinc-500 mt-2 mb-1">{lang === "fr" ? "Étapes" : "Steps"}</h4>
                        <ol className="list-decimal list-inside space-y-0.5 text-zinc-700">
                          {p.content.steps.map((s, i) => <li key={i}>{typeof s === "string" ? s : (s.text || JSON.stringify(s))}</li>)}
                        </ol>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
